'use client';

import { c as _c } from "react/compiler-runtime";
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React from 'react';
import { CookiesProvider } from 'react-cookie';
import { NavermapsProvider } from 'react-naver-maps';
import { ENV, IS_DEV } from '@/constants';
import type { SUB_DOMAIN } from '@/constants/domain';
import { MswProvider } from '@/mocks/MswProvider';
import { GlobalController } from '@/modules';
import { DeviceInfoStoreProvider, useMountDeviceInfoStore } from '@/stores/DeviceStore';
import { cookies } from '@/utils/cookies';
import { ChannelTalk } from '../ChannelTalk';
import { AuthProvider } from './_components/AuthProvider';
import { BenefitEntryConsentController } from './_components/BenefitEntryConsentController';
import { GuestProvider } from './_components/GuestProvider';
import { HistoryObserver } from './_components/HistoryObserver';
import { JoinController } from './_components/JoinController';
import { PageViewEventLogger } from './_components/PageViewEventLogger';
import { useGlobalEventListener } from './_hooks/useGlobalEventListener';
import { useGtmEventClickListener } from './_hooks/useGtmEventClickListener';
import { useLoginGtmEventListener } from './_hooks/useLoginGtmEventListener';
interface GlobalProviderProps {
  children: React.ReactNode;
  initialSubDomain?: SUB_DOMAIN | null;
}
const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onSuccess: async (_data, _variables, _context, mutation) => {
      if (mutation.meta) {
        const {
          reset,
          invalidates
        } = mutation.meta;
        if (reset) {
          queryClient.resetQueries({
            predicate: query => {
              const hasIncludeQueryKey = reset.some(queryKeys => {
                const hasAllQueryKey = queryKeys.every(queryKey => query.queryKey.includes(queryKey));
                return hasAllQueryKey;
              });
              return hasIncludeQueryKey;
            }
          });
        }
        if (invalidates) {
          await queryClient.invalidateQueries({
            predicate: query => {
              const hasIncludeQueryKey = invalidates.some(queryKeys => {
                const hasAllQueryKey = queryKeys.every(queryKey => query.queryKey.includes(queryKey));
                return hasAllQueryKey;
              });
              return hasIncludeQueryKey;
            }
          });
        }
      }
    }
  }),
  defaultOptions: {
    queries: {
      staleTime: 300_000,
      retry: 2,
      retryDelay: 1_000
    }
  }
});
const GlobalProviderContents = t0 => {
  const $ = _c(12);
  const {
    children
  } = t0;
  useMountDeviceInfoStore();
  useGlobalEventListener();
  useGtmEventClickListener();
  useLoginGtmEventListener();
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      path: "/"
    };
    t2 = <PageViewEventLogger />;
    t3 = <HistoryObserver />;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  let t5;
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <BenefitEntryConsentController />;
    t5 = <GlobalController />;
    t6 = <JoinController />;
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
  } else {
    t4 = $[3];
    t5 = $[4];
    t6 = $[5];
  }
  let t7;
  if ($[6] !== children) {
    t7 = <AuthProvider><GuestProvider>{t4}{t5}{t6}<NavermapsProvider ncpClientId={ENV.NEXT_PUBLIC_NAVER_CLOUD_CLIENT_ID}>{children}</NavermapsProvider></GuestProvider></AuthProvider>;
    $[6] = children;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = IS_DEV ? <ReactQueryDevtools buttonPosition="bottom-left" initialIsOpen={false} /> : null;
    t9 = <ChannelTalk />;
    $[8] = t8;
    $[9] = t9;
  } else {
    t8 = $[8];
    t9 = $[9];
  }
  let t10;
  if ($[10] !== t7) {
    t10 = <MswProvider><QueryClientProvider client={queryClient}><CookiesProvider cookies={cookies} defaultSetOptions={t1}>{t2}{t3}{t7}{t8}{t9}</CookiesProvider></QueryClientProvider></MswProvider>;
    $[10] = t7;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  return t10;
};
export const GlobalProvider = t0 => {
  const $ = _c(5);
  const {
    children,
    initialSubDomain
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <GlobalProviderContents>{children}</GlobalProviderContents>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== initialSubDomain || $[3] !== t1) {
    t2 = <DeviceInfoStoreProvider initialSubDomain={initialSubDomain}>{t1}</DeviceInfoStoreProvider>;
    $[2] = initialSubDomain;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};